import { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Maximize2, ZoomIn, ZoomOut, Eye } from "lucide-react";

// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: true,
  theme: "base",
  themeVariables: {
    primaryColor: "#3b82f6",
    primaryTextColor: "#1e293b",
    primaryBorderColor: "#2563eb",
    lineColor: "#64748b",
    secondaryColor: "#f1f5f9",
    tertiaryColor: "#e0e7ff",
    fontSize: "16px",
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
    // Dark mode will be handled via CSS
    background: "transparent",
    mainBkg: "#ffffff",
    textColor: "#1e293b",
    borderColor: "#cbd5e1",
    nodeBorder: "#64748b",
    clusterBkg: "#f8fafc",
    clusterBorder: "#94a3b8",
    titleColor: "#0f172a",
  },
  flowchart: {
    htmlLabels: false,
    curve: "basis",
    padding: 30,
    nodeSpacing: 80,
    rankSpacing: 70,
    useMaxWidth: false,
  },
  sequence: {
    diagramMarginX: 40,
    diagramMarginY: 30,
    boxMargin: 15,
    boxTextMargin: 10,
    noteMargin: 15,
    messageMargin: 55,
    actorMargin: 100,
    width: 220,
    height: 50,
    useMaxWidth: false,
  },
  gantt: {
    fontSize: 14,
    numberSectionStyles: 4,
    axisFormat: "%m/%d",
  },
});

interface MermaidDiagramProps {
  chart: string;
  id?: string;
  enableZoom?: boolean;
  /** Hide inline preview — only show a button that opens the fullscreen modal */
  modalOnly?: boolean;
  /** Label for the modal-only button */
  buttonLabel?: string;
}

export function MermaidDiagram({ chart, id, enableZoom = true, modalOnly = false, buttonLabel = "Show Diagram" }: MermaidDiagramProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [error, setError] = useState<string | null>(null);

  const renderDiagram = (container: HTMLDivElement) => {
    container.innerHTML = "";
    setError(null);

    const div = document.createElement("div");
    div.className = "mermaid";
    div.textContent = chart;
    container.appendChild(div);

    mermaid.run({
      nodes: [div],
    }).catch((err) => {
      console.error("Mermaid rendering error:", err);
      setError(err.message);
      container.innerHTML = `<div class="text-destructive p-4 border border-destructive/20 rounded-lg bg-destructive/5">
        <p class="font-semibold">Error rendering diagram</p>
        <p class="text-sm mt-1">${err.message}</p>
      </div>`;
    });
  };

  useEffect(() => {
    if (!modalOnly && ref.current) {
      renderDiagram(ref.current);
    }
  }, [chart, id, modalOnly]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 400));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 25));
  const handleResetZoom = () => setZoom(100);

  return (
    <>
      {modalOnly ? (
        <Button
          variant="outline"
          className="w-full h-12 gap-2"
          onClick={() => setIsModalOpen(true)}
        >
          <Eye className="h-5 w-5" />
          {buttonLabel}
        </Button>
      ) : (
        <div className="relative group">
          <div
            ref={ref}
            className="my-6 overflow-auto p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700 min-h-[200px] [&_svg]:min-w-max dark:[&_svg]:filter dark:[&_svg]:invert dark:[&_svg]:hue-rotate-180"
          />
          {enableZoom && !error && (
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setIsModalOpen(true)}
            >
              <Maximize2 className="h-4 w-4 mr-2" />
              Expand
            </Button>
          )}
        </div>
      )}

      {(enableZoom || modalOnly) && (
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] overflow-hidden flex flex-col">
            <DialogHeader className="shrink-0">
              <div className="flex items-center justify-between">
                <DialogTitle>Diagram Viewer</DialogTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 25}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium w-16 text-center">{zoom}%</span>
                  <Button variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 400}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleResetZoom}>
                    Reset
                  </Button>
                </div>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-900/50 rounded-lg">
              <div
                className="flex justify-center p-6 min-h-full"
                style={{ transform: `scale(${zoom / 100})`, transformOrigin: "top center" }}
              >
                <div
                  ref={(node) => {
                    if (node && isModalOpen) {
                      renderDiagram(node);
                    }
                  }}
                  className="[&_.mermaid]:max-w-full [&_svg]:max-w-full dark:[&_svg]:filter dark:[&_svg]:invert dark:[&_svg]:hue-rotate-180"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
